import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react'
import clsx from 'clsx'
import styles from './Popover.module.css'

/**
 * The community popover primitive: one disclosure group (which popover is open, how hover,
 * focus, click, Escape and a pointer-down outside move it) and one floating panel (fixed from
 * its anchor's rect: above it, clamped to the viewport, flipped below when there is no room; or
 * always below, `prefer`). The badge popover (`BadgePopover`, click-only: it spreads `onClick`
 * alone) and the member hover card (`MemberCard`) are both built on it; VerifiedCheck's tooltip
 * is still its own copy (see the Directive in the commit that made this file).
 *
 * The group is a tiny external store, not component state, so one group can be shared by
 * components that have no common parent (every author line on a page shares the member-card
 * group: one card open at a time) and so its rules are testable without a DOM.
 */

// ── the disclosure group ──────────────────────────────────────────────────────

export interface DisclosureState {
  id: string
  /** Opened by click/tap/Enter: survives mouseleave and blur. */
  pinned: boolean
}

/** Per-trigger behaviour. The defaults are the badge popover's. */
export interface TriggerOptions {
  /** Hover intent: the pointer must rest this long before the popover opens. 0 = at once. */
  openDelayMs?: number
  /** The panel holds content the reader may reach (a link): leaving the trigger closes after a
   *  short grace instead of at once, and focus moving into the panel keeps it open. */
  interactive?: boolean
  /** A click pins the popover open (badge tiles). false: the click is left alone — a trigger
   *  wrapping a link keeps navigating. */
  clickPins?: boolean
  /** Called once each time this trigger's popover opens (e.g. to start a fetch). */
  onOpen?: () => void
  /** Called when the pointer has rested `warmDelayMs` on the trigger — inside the hover-intent
   *  window, before `onOpen` — e.g. to start the fetch the open will need. Leaving before then
   *  cancels it, so a pointer passing over a feed warms nothing. Hover only; never on focus. */
  onWarm?: () => void
  /** The rest before `onWarm`; must be shorter than `openDelayMs`. */
  warmDelayMs?: number
}

/** The attribute every trigger zone carries; a pointer-down outside all of them closes. */
export const POPOVER_ZONE_ATTR = 'data-popover-zone'

/** How long an interactive popover survives the pointer leaving its trigger (crossing the gap
 *  to the panel). */
export const POPOVER_GRACE_MS = 150

const canHoverMedia = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(hover: hover)').matches

type Update = (cur: DisclosureState | null) => DisclosureState | null

export class DisclosureGroup {
  private state: DisclosureState | null = null
  private readonly listeners = new Set<() => void>()
  private openTimer: ReturnType<typeof setTimeout> | null = null
  private warmTimer: ReturnType<typeof setTimeout> | null = null
  private closeTimer: ReturnType<typeof setTimeout> | null = null
  private docBound = false
  private readonly canHover: () => boolean
  private readonly graceMs: number

  constructor(opts: { canHover?: () => boolean; graceMs?: number } = {}) {
    this.canHover = opts.canHover ?? canHoverMedia
    this.graceMs = opts.graceMs ?? POPOVER_GRACE_MS
  }

  getState = (): DisclosureState | null => this.state

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  /** Click / tap / Enter / Space: pin, or close a pinned one. */
  click(id: string, opts: TriggerOptions = {}): void {
    this.clearTimers()
    this.set(cur => (cur?.id === id && cur.pinned ? null : { id, pinned: true }), id, opts)
  }

  /** Hover devices only; a pinned popover is not stolen by passing the mouse over another. */
  hoverEnter(id: string, opts: TriggerOptions = {}): void {
    if (!this.canHover()) {
      return
    }
    this.clearClose()
    const open = (): void => {
      this.openTimer = null
      this.set(cur => (cur?.pinned ? cur : { id, pinned: false }), id, opts)
    }
    const delay = opts.openDelayMs ?? 0
    if (delay <= 0) {
      open()
      return
    }
    if (this.state?.id === id) {
      return
    }
    this.clearOpen()
    this.openTimer = setTimeout(open, delay)
    const { onWarm } = opts
    if (onWarm) {
      this.warmTimer = setTimeout(() => {
        this.warmTimer = null
        onWarm()
      }, opts.warmDelayMs ?? Math.floor(delay / 2))
    }
  }

  /** `immediate`: the pointer went straight onto another control — no grace. */
  hoverLeave(id: string, opts: TriggerOptions = {}, immediate = false): void {
    this.clearOpen()
    const close: Update = cur => (cur?.id === id && !cur.pinned ? null : cur)
    if (opts.interactive && !immediate && this.state?.id === id) {
      this.clearClose()
      this.closeTimer = setTimeout(() => {
        this.closeTimer = null
        this.set(close)
      }, this.graceMs)
      return
    }
    this.set(close)
  }

  /** Focus is a deliberate move: it replaces whatever is open, unless it is this one. */
  focus(id: string, opts: TriggerOptions = {}): void {
    if (!this.canHover()) {
      return
    }
    this.clearTimers()
    this.set(cur => (cur?.id === id ? cur : { id, pinned: false }), id, opts)
  }

  blur(id: string): void {
    this.set(cur => (cur?.id === id && !cur.pinned ? null : cur))
  }

  close(): void {
    this.clearTimers()
    this.set(() => null)
  }

  /** A trigger going away closes its own popover (and only its own). */
  closeIf(id: string): void {
    if (this.state?.id === id) {
      this.close()
    }
  }

  private set(update: Update, openedId?: string, opts?: TriggerOptions): void {
    const prev = this.state
    const next = update(prev)
    if (next === prev) {
      return
    }
    this.state = next
    this.bindDocument(next !== null)
    if (next && openedId === next.id && prev?.id !== next.id) {
      opts?.onOpen?.()
    }
    this.listeners.forEach(fn => fn())
  }

  // One set of document listeners per group, only while something is open.
  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      this.close()
    }
  }

  private readonly onDown = (e: PointerEvent): void => {
    const target = e.target as Element | null
    if (!target?.closest?.(`[${POPOVER_ZONE_ATTR}]`)) {
      this.close()
    }
  }

  private bindDocument(on: boolean): void {
    if (typeof document === 'undefined' || on === this.docBound) {
      return
    }
    this.docBound = on
    if (on) {
      document.addEventListener('keydown', this.onKey)
      document.addEventListener('pointerdown', this.onDown)
    } else {
      document.removeEventListener('keydown', this.onKey)
      document.removeEventListener('pointerdown', this.onDown)
    }
  }

  private clearOpen(): void {
    if (this.warmTimer) {
      clearTimeout(this.warmTimer)
      this.warmTimer = null
    }
    if (this.openTimer) {
      clearTimeout(this.openTimer)
      this.openTimer = null
    }
  }

  private clearClose(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer)
      this.closeTimer = null
    }
  }

  private clearTimers(): void {
    this.clearOpen()
    this.clearClose()
  }
}

export interface TriggerProps {
  onClick?: () => void
  onMouseEnter: () => void
  onMouseLeave: (e: React.MouseEvent) => void
  onFocus: () => void
  onBlur: (e: React.FocusEvent) => void
}

const INTERACTIVE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]'

/**
 * Subscribe to a disclosure group. Without `shared`, the calling component owns a fresh group
 * for its lifetime (the badge grid); with it, every caller sees the same one (the member cards).
 */
export function useDisclosureGroup(shared?: DisclosureGroup): {
  openId: string | null
  group: DisclosureGroup
  triggerProps: (id: string, opts?: TriggerOptions) => TriggerProps
} {
  const [own] = useState(() => (shared ? null : new DisclosureGroup()))
  const group = (shared ?? own) as DisclosureGroup
  useEffect(() => (own ? () => own.close() : undefined), [own])
  const state = useSyncExternalStore(group.subscribe, group.getState, () => null)

  const triggerProps = useCallback(
    (id: string, opts: TriggerOptions = {}): TriggerProps => {
      const props: TriggerProps = {
        onMouseEnter: () => group.hoverEnter(id, opts),
        onMouseLeave: e => {
          // Straight onto another control (the check beside a handle): it is the reader's
          // next thing — no grace, so two popovers never show for one gesture.
          const to = e.relatedTarget
          const immediate =
            to instanceof Element &&
            !to.closest(`[${POPOVER_ZONE_ATTR}]`) &&
            Boolean(to.closest(INTERACTIVE_SELECTOR))
          group.hoverLeave(id, opts, immediate)
        },
        onFocus: () => group.focus(id, opts),
        onBlur: e => {
          // Focus moving into an interactive panel (it lives inside the trigger zone) stays.
          const to = e.relatedTarget
          if (opts.interactive && to instanceof Node && e.currentTarget.contains(to)) {
            return
          }
          group.blur(id)
        },
      }
      if (opts.clickPins !== false) {
        props.onClick = () => group.click(id, opts)
      }
      return props
    },
    [group]
  )

  return { openId: state?.id ?? null, group, triggerProps }
}

// ── position: fixed from the anchor's rect — above it, clamped to the viewport, below if no room ──

const GUTTER = 16
const GAP = 8

interface Place {
  left: number
  below: boolean
  /** `top` when below the anchor; `bottom` (from the viewport's bottom edge) when above, so a
   *  panel that grows as its content arrives grows away from the anchor. */
  edge: number
}

export const FloatingPanel: React.FC<{
  children: React.ReactNode
  id: string
  /** The element the panel hangs from: its viewport rect places the panel. */
  anchor: React.RefObject<HTMLElement>
  /** true (the default): a labelled non-modal dialog (the badge disclosure). false: no role —
   *  the panel is only a description target (`aria-describedby`) with no focus stop of its own. */
  dialog?: boolean
  labelledBy?: string
  /** `span` when the panel sits inside phrasing content (an author line in a `<p>`). */
  as?: 'div' | 'span'
  className?: string
  /** `above` (the default, the member card): above the anchor, flipped below when there is no
   *  room. `below`: always under the anchor — the badge popover, which must never cover the
   *  member's header above the badges. */
  prefer?: 'above' | 'below'
}> = ({
  id,
  labelledBy,
  anchor,
  dialog = true,
  as: Tag = 'div',
  className,
  prefer = 'above',
  children,
}) => {
  const panel = useRef<HTMLElement>(null)
  const [place, setPlace] = useState<Place | null>(null)

  // `position: fixed` from the anchor's rect (as VerifiedCheck's tooltip), not absolute inside
  // it: an author line sits in boxes that clip their overflow — a best-list pick block
  // (`overflow: hidden` for its corners), the lab drawer's scroll region, the phone sheet — and
  // an absolute card hanging past their edge was cut off (2026-10-21). Fixed, it is clipped by
  // nothing, so it is re-placed when anything scrolls or the viewport resizes, and when it grows
  // (the member card's facts arrive after it opens: the fit-above check must see the full height).
  useLayoutEffect(() => {
    const measure = (): void => {
      const a = anchor.current
      const p = panel.current
      if (!a || !p) {
        return
      }
      const box = a.getBoundingClientRect()
      const width = p.offsetWidth
      const height = p.offsetHeight
      const root = document.documentElement
      const centred = box.left + box.width / 2 - width / 2
      const left = Math.max(GUTTER, Math.min(centred, root.clientWidth - GUTTER - width))
      const below = prefer === 'below' || box.top - height - GAP < GUTTER
      const edge = below ? box.bottom + GAP : root.clientHeight - box.top + GAP
      setPlace(cur => {
        const same = cur && cur.left === left && cur.below === below && cur.edge === edge
        return same ? cur : { left, below, edge }
      })
    }
    measure()
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, { passive: true, capture: true })
    const grown = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    if (grown && panel.current) {
      grown.observe(panel.current)
    }
    return () => {
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, { capture: true })
      grown?.disconnect()
    }
  }, [anchor, prefer])

  let style: React.CSSProperties = { left: 0, top: 0, visibility: 'hidden' }
  if (place) {
    style = place.below
      ? { left: place.left, top: place.edge }
      : { left: place.left, bottom: place.edge }
  }
  return (
    <Tag
      ref={panel as React.RefObject<HTMLDivElement & HTMLSpanElement>}
      id={id}
      role={dialog ? 'dialog' : undefined}
      aria-labelledby={labelledBy}
      className={clsx(styles.panel, className)}
      style={style}
    >
      {children}
    </Tag>
  )
}
